/**
 * O controle da escala horizontal de um gráfico de resultados do CPTu (onda 347; spec 10 §4.8, R12; a prévia aprovada
 * pelo Humberto em 22/10, quadro 5): o botão «Escala» no título do gráfico abre a janela «Escala · qt (MPa)» com
 *  · **Tipo**: Linear | Log — o log só para qc, qt, fs, Rf e Su; nas outras, a opção fica bloqueada com o motivo;
 *  · **Limites**: Automático | Fixo, com o **Mínimo** e o **Máximo** em número brasileiro, nas unidades da grandeza;
 *  · **Divisões**: automáticas | o passo digitado (só no linear);
 *  · a nota (fica no aparelho, não muda o relatório) e **«Voltar ao automático»**.
 *
 * O que se escolhe vale na hora; valor fora da regra fica vermelho, com a frase, e não se aplica. A janela é o
 * `Dialogo` da casa (SP-347d, opção A do Humberto): a peça mora nesta pasta, que a bancada e o Portal têm byte a byte,
 * e o `ui.tsx` da bancada a reexporta. Os botões são os da casa (`.botao`), no rodapé `.rodape-dialogo` — o secundário
 * («Voltar ao automático») antes da ação («Fechar») — e só o corpo rola (P-JAN-01 a 04).
 */
import type { JSX } from 'react';
import { useId, useState } from 'react';
import { Dialogo } from './Dialogo';
import {
  ESCALA_AUTOMATICA, GRANDEZAS_COM_LOG, escalaEhAutomatica, numeroDigitado, validarEscala,
} from './escalaDoGrafico';
import type { EscalaDoGrafico } from './escalaDoGrafico';
import { numeroBr } from './escalas';

/** O rascunho da janela: os textos como foram digitados. */
interface Rascunho {
  tipo: 'linear' | 'log';
  fixo: boolean;
  min: string;
  max: string;
  comPasso: boolean;
  passo: string;
}

/** O número escrito para o campo, sem casas sobrando («5», «0,25»). */
const paraCampo = (v: number): string => {
  if (!Number.isFinite(v)) return '';
  const casas = Math.min(6, Math.max(0, (String(v).split('.')[1] ?? '').length));
  return numeroBr(v, casas);
};

/** Três algarismos significativos: o domínio dos dados vira um número que se digita. */
const arredondado = (v: number): number => (Number.isFinite(v) && v !== 0 ? Number(v.toPrecision(3)) : v);

function rascunhoDe(e: EscalaDoGrafico): Rascunho {
  const l = e.limites === 'auto' ? null : e.limites;
  return {
    tipo: e.tipo, fixo: !!l, min: l ? paraCampo(l.min) : '', max: l ? paraCampo(l.max) : '',
    comPasso: e.passo !== null, passo: e.passo !== null ? paraCampo(e.passo) : '',
  };
}

/** A escala do rascunho e o erro (null: vale) — e qual campo está errado. */
function escalaDe(r: Rascunho, grandeza: string): { e: EscalaDoGrafico; erro: string | null; campo: 'min' | 'max' | 'passo' | 'tipo' | null } {
  const limites = r.fixo ? { min: numeroDigitado(r.min), max: numeroDigitado(r.max) } : 'auto' as const;
  const passo = r.comPasso && r.tipo === 'linear' ? numeroDigitado(r.passo) : null;
  const e: EscalaDoGrafico = { tipo: r.tipo, limites, passo };
  const erro = validarEscala(e, grandeza);
  if (!erro) return { e, erro: null, campo: null };
  const campo = /log só vale/.test(erro) ? 'tipo'
    : /divisões/.test(erro) ? 'passo'
      : limites !== 'auto' && !Number.isFinite(limites.min) ? 'min'
        : /maior que zero|menor que o máximo/.test(erro) ? 'min' : 'max';
  return { e, erro, campo };
}

export function ControleDaEscala(p: {
  /** A chave da grandeza (a da trilha ou da sobreposição: `qt`, `fs`, `u2`…). */
  grandeza: string;
  /** O rótulo («qt», «u₂»). */
  rotulo: string;
  unidade: string;
  /** A escala em uso; null ou ausente: a automática. */
  escala?: EscalaDoGrafico | null;
  /** O domínio desenhado agora: ao passar a «Fixo» com os campos vazios, eles começam nele. */
  dominio?: readonly [number, number];
  aoMudar(e: EscalaDoGrafico | null): void;
}): JSX.Element {
  const id = useId();
  const atual = p.escala ?? ESCALA_AUTOMATICA(p.grandeza);
  const [aberta, setAberta] = useState(false);
  const [r, setR] = useState<Rascunho>(() => rascunhoDe(atual));
  const comLog = GRANDEZAS_COM_LOG.includes(p.grandeza);
  const titulo = `Escala · ${p.rotulo}${p.unidade ? ` (${p.unidade})` : ''}`;
  const unidade = p.unidade ? ` (${p.unidade})` : '';
  const ajustada = !escalaEhAutomatica(p.escala);
  const conta = escalaDe(r, p.grandeza);

  const abrir = (): void => {
    setR(rascunhoDe(atual));
    setAberta(true);
  };
  /** Muda o rascunho e, se a escala vale, aplica na hora. */
  const mudar = (x: Partial<Rascunho>): void => {
    const novo = { ...r, ...x };
    setR(novo);
    const c = escalaDe(novo, p.grandeza);
    if (!c.erro) p.aoMudar(escalaEhAutomatica(c.e) ? null : c.e);
  };
  const voltar = (): void => {
    setR(rascunhoDe(ESCALA_AUTOMATICA(p.grandeza)));
    p.aoMudar(null);
  };
  const errado = (campo: 'min' | 'max' | 'passo' | 'tipo'): boolean => conta.campo === campo;

  return (
    <span className="cptu-escala">
      <button type="button" className={`cptu-escala-botao${ajustada ? ' ajustada' : ''}`} data-grandeza={p.grandeza}
        aria-haspopup="dialog" aria-label={`${titulo}${ajustada ? ' — ajustada' : ''}`} onClick={abrir}>
        Escala{ajustada ? ' ●' : ''}
      </button>
      <Dialogo aberto={aberta} titulo={titulo} classe="cptu-escala-janela" dados={{ grandeza: p.grandeza }}
        aoFechar={() => setAberta(false)}>
        {aberta ? (
          <>

            <fieldset className="cptu-escala-grupo">
              <legend>Tipo</legend>
              <label className="cptu-escala-opcao">
                <input type="radio" name={`${id}-tipo`} value="linear" checked={r.tipo === 'linear'} onChange={() => mudar({ tipo: 'linear' })} />
                {' '}Linear
              </label>
              <label className="cptu-escala-opcao">
                <input type="radio" name={`${id}-tipo`} value="log" checked={r.tipo === 'log'} disabled={!comLog}
                  aria-describedby={comLog ? undefined : `${id}-sem-log`} onChange={() => mudar({ tipo: 'log', comPasso: false })} />
                {' '}Log
              </label>
              {!comLog ? <p id={`${id}-sem-log`} className="cptu-nota">O eixo log só vale para qc, qt, fs, Rf e Su (grandezas sempre positivas).</p> : null}
            </fieldset>

            <fieldset className="cptu-escala-grupo">
              <legend>Limites</legend>
              <label className="cptu-escala-opcao">
                <input type="radio" name={`${id}-limites`} value="auto" checked={!r.fixo} onChange={() => mudar({ fixo: false })} />
                {' '}Automático
              </label>
              <label className="cptu-escala-opcao">
                <input type="radio" name={`${id}-limites`} value="fixo" checked={r.fixo} onChange={() => mudar({
                    fixo: true,
                    min: r.min || (p.dominio ? paraCampo(arredondado(p.dominio[0])) : ''),
                    max: r.max || (p.dominio ? paraCampo(arredondado(p.dominio[1])) : ''),
                  })} />
                {' '}Fixo
              </label>
              <div className="cptu-escala-campos">
                <label className="cptu-escala-campo">
                  <span>Mínimo{unidade}</span>
                  <input type="text" inputMode="decimal" name="minimo" value={r.min} disabled={!r.fixo}
                    aria-invalid={r.fixo && errado('min') ? true : undefined} aria-describedby={`${id}-erro`}
                    onChange={(ev) => mudar({ min: ev.target.value })} />
                </label>
                <label className="cptu-escala-campo">
                  <span>Máximo{unidade}</span>
                  <input type="text" inputMode="decimal" name="maximo" value={r.max} disabled={!r.fixo}
                    aria-invalid={r.fixo && errado('max') ? true : undefined} aria-describedby={`${id}-erro`}
                    onChange={(ev) => mudar({ max: ev.target.value })} />
                </label>
              </div>
            </fieldset>

            <fieldset className="cptu-escala-grupo">
              <legend>Divisões</legend>
              <label className="cptu-escala-opcao">
                <input type="radio" name={`${id}-divisoes`} value="auto" checked={!r.comPasso || r.tipo === 'log'} onChange={() => mudar({ comPasso: false })} />
                {' '}Automáticas
              </label>
              <label className="cptu-escala-opcao">
                <input type="radio" name={`${id}-divisoes`} value="passo" checked={r.comPasso && r.tipo === 'linear'} disabled={r.tipo === 'log'}
                  onChange={() => mudar({ comPasso: true })} />
                {' '}A cada
              </label>
              <label className="cptu-escala-campo">
                <span>Passo{unidade}</span>
                <input type="text" inputMode="decimal" name="passo" value={r.passo} disabled={!r.comPasso || r.tipo === 'log'}
                  aria-invalid={r.comPasso && errado('passo') ? true : undefined} aria-describedby={`${id}-erro`}
                  onChange={(ev) => mudar({ passo: ev.target.value })} />
              </label>
              {r.tipo === 'log' ? <p className="cptu-nota">No eixo log, as divisões são as décadas (0,1 · 1 · 10…).</p> : null}
            </fieldset>

            <p id={`${id}-erro`} className="cptu-escala-erro" role="alert">
              {conta.erro ? `Não se aplica: ${conta.erro}.` : ''}
            </p>
            <p className="cptu-nota">
              A escala vale para {p.rotulo} em todos os gráficos desta tela e fica guardada neste aparelho. Valor fora dos
              limites fixos é cortado na borda, com a marca «▸». Não muda o relatório.
            </p>
            <div className="rodape-dialogo">
              <button type="button" className="botao secundario" onClick={voltar}>Voltar ao automático</button>
              <button type="button" className="botao" onClick={() => setAberta(false)}>Fechar</button>
            </div>
          </>
        ) : null}
      </Dialogo>
    </span>
  );
}
